import { useMemo } from "react";
import { useMatchQBCustomer, useRemoveQBCustomerMapping } from "@/lib/hooks/api/admin";
import { getCompanyQuickBooksCustomerRefs } from "@/lib/utils/crm/quickbooks-company-ref";
import type { Company } from "@/lib/types/database";

// ============================================================================
// INVOICE MAPPINGS HOOK
// ============================================================================

export function useInvoiceMappings(company: Company | null | undefined) {
  const matchQBCustomer = useMatchQBCustomer();
  const removeQBCustomerMapping = useRemoveQBCustomerMapping();

  // Get existing QuickBooks customer mappings for this contact (moved up to avoid hook order issues)
  const existingMappings = useMemo(
    () => (company ? getCompanyQuickBooksCustomerRefs(company) : []),
    [company]
  );

  // Note: Drawer open/close handlers are managed in the parent component

  const handleConfirmQBCustomerMapping = useMemo(
    () => async (qbCustomerId: string, qbCustomerName?: string, qbRealmId?: string) => {
      if (company?.id) {
        matchQBCustomer.mutate({
          qbId: qbCustomerId,
          companyId: company.id,
          quickbooksDisplayName: qbCustomerName,
          quickbooksRealmId: qbRealmId,
        });
      }
    },
    [company?.id, matchQBCustomer]
  );

  const handleRemoveQBCustomerMapping = useMemo(
    () => (qbCustomerId: string) => {
      if (company?.id) {
        removeQBCustomerMapping.mutate({
          companyId: company.id,
          qbCustomerId,
        });
      }
    },
    [company?.id, removeQBCustomerMapping]
  );

  return {
    existingMappings,
    handleConfirmQBCustomerMapping,
    handleRemoveQBCustomerMapping,
    isRemoving: removeQBCustomerMapping.isPending,
  };
}
